import { useState, useRef, useMemo } from "react";
import { Upload, Trash2, Image, Copy, Check, FolderPlus, Folder, FolderOpen, X } from "lucide-react";
import { useAdmin } from "../admin-context";
import { apiUrl } from "../../../utils/config";

const BASE_URL = apiUrl?.replace("/api", "") || "";

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const sanitize = (s) =>
  s.trim().replace(/[^a-z0-9_-]/gi, "_").toLowerCase().slice(0, 60);

const MediaLibraryPage = () => {
  const { mediaFiles, uploadMedia, deleteMedia } = useAdmin();

  const [activeFolder, setActiveFolder] = useState(null); // null = All
  const [uploading, setUploading]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [copied, setCopied]             = useState(null);
  const [dragOver, setDragOver]         = useState(false);
  const [newFolderInput, setNewFolderInput] = useState("");
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);

  const fileInputRef = useRef();

  // Derive folder list from loaded files
  const folders = useMemo(() => {
    const set = new Set(mediaFiles.map((f) => f.folder).filter(Boolean));
    return Array.from(set).sort();
  }, [mediaFiles]);

  const visibleFiles = useMemo(
    () => activeFolder ? mediaFiles.filter((f) => f.folder === activeFolder) : mediaFiles,
    [mediaFiles, activeFolder]
  );

  const doUpload = async (files) => {
    if (!files || files.length === 0) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      await uploadMedia(fd, activeFolder || "general");
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (e) => { doUpload(e.target.files); e.target.value = ""; };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files); };

  const handleCopy = (url) => {
    navigator.clipboard.writeText(`${BASE_URL}${url}`);
    setCopied(url);
    setTimeout(() => setCopied(null), 1500);
  };

  const handleDelete = async (id) => {
    await deleteMedia(id);
    setConfirmDelete(null);
  };

  const handleCreateFolder = () => {
    const name = sanitize(newFolderInput);
    if (!name) return;
    setActiveFolder(name);
    setNewFolderInput("");
    setShowNewFolderModal(false);
  };

  return (
    <>
    <div className="flex gap-6 h-full">

      {/* ── Folder sidebar ─────────────────────────────────────── */}
      <aside className="w-48 flex-shrink-0 space-y-1">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Folders</p>
          <button
            title="New folder"
            onClick={() => { setNewFolderInput(""); setShowNewFolderModal(true); }}
            className="p-1 hover:bg-orange-100 text-orange-500 rounded-lg transition"
          >
            <FolderPlus size={15} />
          </button>
        </div>

        {/* All Files */}
        <button
          onClick={() => setActiveFolder(null)}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition
            ${!activeFolder ? "bg-orange-500 text-white shadow-sm" : "text-gray-600 hover:bg-gray-100"}`}
        >
          <Image size={14} className="flex-shrink-0" />
          <span className="truncate">All Files</span>
          <span className={`ml-auto text-xs font-bold ${!activeFolder ? "text-orange-100" : "text-gray-400"}`}>
            {mediaFiles.length}
          </span>
        </button>

        {folders.map((folder) => {
          const count = mediaFiles.filter((f) => f.folder === folder).length;
          const isActive = activeFolder === folder;
          return (
            <button
              key={folder}
              onClick={() => setActiveFolder(folder)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition
                ${isActive ? "bg-orange-500 text-white shadow-sm" : "text-gray-600 hover:bg-gray-100"}`}
            >
              {isActive ? <FolderOpen size={14} className="flex-shrink-0" /> : <Folder size={14} className="flex-shrink-0" />}
              <span className="truncate">{folder}</span>
              <span className={`ml-auto text-xs font-bold ${isActive ? "text-orange-100" : "text-gray-400"}`}>{count}</span>
            </button>
          );
        })}
      </aside>

      {/* ── Main area ──────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {activeFolder ? activeFolder : "Media Library"}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {visibleFiles.length} image{visibleFiles.length !== 1 ? "s" : ""}
              {activeFolder && <span className="text-gray-400"> in this folder</span>}
            </p>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-xl font-bold shadow-lg shadow-orange-200 transition active:scale-95 text-sm disabled:opacity-60"
          >
            <Upload size={16} />
            {uploading ? "Uploading…" : `Upload${activeFolder ? ` to "${activeFolder}"` : ""}`}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileInput} />
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-6 flex items-center gap-4 cursor-pointer transition
            ${dragOver ? "border-orange-400 bg-orange-50" : "border-gray-200 hover:border-orange-300 hover:bg-orange-50/40"}`}
        >
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Upload size={18} className="text-orange-500" />
          </div>
          <div>
            <p className="font-bold text-gray-700 text-sm">Drop images here or click to browse</p>
            <p className="text-xs text-gray-400">
              {activeFolder
                ? `Files will be added to folder "${activeFolder}"`
                : 'Files will go to "general". Select a folder first to target it.'}
            </p>
          </div>
        </div>

        {/* Grid */}
        {visibleFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
            <Folder size={40} className="opacity-20" />
            <p className="text-sm">
              {activeFolder ? `No images in "${activeFolder}" yet.` : "No images yet. Upload your first image."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {visibleFiles.map((file) => {
              const fullUrl = `${BASE_URL}${file.url}`;
              return (
                <div
                  key={file.id}
                  className="group relative bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="aspect-square bg-gray-50">
                    <img src={fullUrl} alt={file.name} className="w-full h-full object-cover" />
                  </div>

                  <div className="p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="text-[9px] text-gray-400 font-mono">#{file.id}</span>
                      <span className="text-[9px] bg-gray-100 text-gray-500 px-1 rounded font-medium truncate max-w-[70px]">{file.folder}</span>
                    </div>
                    <p className="text-[10px] text-gray-600 truncate font-medium" title={file.name}>{file.name}</p>
                    <p className="text-[9px] text-gray-400">{formatSize(file.size)}</p>
                  </div>

                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => handleCopy(file.url)}
                      className="bg-white text-gray-700 hover:bg-orange-500 hover:text-white rounded-lg p-2 transition shadow"
                      title="Copy URL"
                    >
                      {copied === file.url ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                    </button>

                    {confirmDelete === file.id ? (
                      <div className="bg-white rounded-lg p-2 flex gap-1 shadow">
                        <button type="button" onClick={() => handleDelete(file.id)}
                          className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded font-bold">Delete</button>
                        <button type="button" onClick={() => setConfirmDelete(null)}
                          className="bg-gray-200 text-gray-700 text-[10px] px-2 py-0.5 rounded font-bold">Cancel</button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(file.id)}
                        className="bg-white text-gray-700 hover:bg-red-500 hover:text-white rounded-lg p-2 transition shadow"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>

    {/* New Folder Modal */}
    {showNewFolderModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <FolderPlus size={18} className="text-orange-500" /> New Folder
            </h3>
            <button
              onClick={() => setShowNewFolderModal(false)}
              className="p-1 hover:bg-gray-100 rounded-full transition"
            >
              <X size={16} className="text-gray-400" />
            </button>
          </div>
          <input
            autoFocus
            value={newFolderInput}
            onChange={(e) => setNewFolderInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateFolder();
              if (e.key === "Escape") setShowNewFolderModal(false);
            }}
            placeholder="e.g. products, logos, banners"
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 mb-4"
          />
          <p className="text-xs text-gray-400 mb-4">
            Spaces and special characters will be replaced with underscores.
            {newFolderInput && sanitize(newFolderInput) && (
              <span className="ml-1 font-mono text-orange-500">→ "{sanitize(newFolderInput)}"</span>
            )}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowNewFolderModal(false)}
              className="flex-1 py-2 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateFolder}
              disabled={!sanitize(newFolderInput)}
              className="flex-1 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold transition text-sm disabled:opacity-40"
            >
              Create
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default MediaLibraryPage;
